<template>
  <div>
    <b-overlay :show="formShow" rounded="true" :fixed="true" no-fade>
      <b-card>
        <!-- Page Header -->
        <b-row class="mb-1">
          <b-col cols="12">
            <div class="d-flex justify-content-between align-items-center">
              <h3 class="mb-0 text-primary">
                <feather-icon icon="BarChart3Icon" size="24" class="mr-2" />
                Quality Review Report
              </h3>
              <div class="d-flex">
                <b-button
                  type="submit"
                  @click="showArchiveRecords()"
                  variant="warning"
                  size="md"
                  class="mr-2"
                >
                  {{
                    archive == "N"
                      ? "Show with archive records"
                      : "Hide with archive records"
                  }}
                </b-button>

                <b-button
                  @click="downloadPdf()"
                  v-show="$Can('view_review_report')"
                  variant="outline-primary"
                  size="md"
                  :disabled="
                    !(resultData.data && resultData.data.length > 0) ||
                      !selectedEvent ||
                      formShow ||
                      download ||
                      !exportReady
                  "
                  class="btn-report text-primary bg-white"
                >
                  <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
                  Generate Report
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Event Selection and Date Range - Always visible at top -->
        <b-row class="mb-1" style="border-bottom: 1px solid #e9ecef;">
          <b-col cols="12">
            <b-row class="align-items-end mb-1">
              <b-col xl="5" lg="5" md="6" sm="12" class="mb-1">
                <b-form-group label="Select Event" label-size="sm" class="mb-0">
                  <v-select
                    id="event"
                    v-model="selectedEvent"
                    :options="eventTypes"
                    :reduce="(val) => val"
                    :filterable="true"
                    placeholder="Search by event..."
                    @option:selected="selectedEvents"
                    label="title"
                    :clearable="false"
                    input-id="event"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" lg="3" md="6" sm="12" class="mb-1">
                <b-form-group label="Date Range" label-size="sm" class="mb-0">
                  <b-input-group size="sm">
                    <flat-pickr
                      :disabled="!resultData.data.length"
                      placeholder="Select date range..."
                      v-model="rangeDate"
                      onClose="testClose"
                      class="form-control"
                      :config="{ mode: 'range', dateFormat: 'd/m/Y' }"
                    />
                    <b-input-group-append>
                      <b-button
                        @click="clearDate()"
                        variant="outline-secondary"
                        size="sm"
                        v-b-tooltip.hover
                        title="Clear date filter"
                      >
                        <feather-icon icon="XIcon" size="12" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col xl="4" lg="4" md="6" sm="12" class="mb-1">
                <b-form-group
                  label="Department / Site"
                  label-size="sm"
                  class="mb-0"
                >
                  <treeselect
                    v-model="selectedDepartmentSites"
                    :multiple="true"
                    :options="departments"
                    placeholder="All departments and sites..."
                    :flat="false"
                    :disable-branch-nodes="false"
                    value-consists-of="BRANCH_PRIORITY"
                    @input="onDepartmentSiteChange"
                    :limit="2"
                    :limitText="(count) => `and ${count} more`"
                    class="treeselect-sm"
                  >
                    <label
                      slot="value-label"
                      slot-scope="{ node }"
                      class="vue-treeselect__label"
                    >
                      {{ node.label }}
                    </label>
                  </treeselect>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <!-- Tab System -->
        <b-row>
          <b-col cols="12">
            <b-tabs content-class="mt-3" justified v-model="activeTabIndex">
              <!-- Dashboard Tab -->
              <b-tab title="Dashboard" active>
                <template #title>
                  <feather-icon icon="BarChart3Icon" size="16" class="mr-1" />
                  Dashboard
                </template>
                <dashboard-tab :result-data="resultData" :is-active="activeTabIndex === 0" />
              </b-tab>

              <!-- Overall Summary Report Tab -->
              <b-tab title="Overall Summary Report">
                <template #title>
                  <feather-icon icon="FileTextIcon" size="16" class="mr-1" />
                  Overall Summary Report
                </template>
                <summary-report-tab
                  ref="summaryReportTab"
                  :result-data="resultData"
                  :filter-options="filterOptions"
                  :table-show="tableShow"
                  @filter-change="handleFilterChange"
                />
              </b-tab>

              <!-- Manager Report Tab -->
              <b-tab title="Manager Report">
                <template #title>
                  <feather-icon icon="UsersIcon" size="16" class="mr-1" />
                  Manager Report
                </template>
                <manager-report-tab
                  ref="managerReportTab"
                  :result-data="resultData"
                  :filter-options="filterOptions"
                  :table-show="tableShow"
                  @filter-change="handleFilterChange"
                />
              </b-tab>

              <!-- Staff Report Tab -->
              <b-tab title="Staff Report">
                <template #title>
                  <feather-icon icon="UserIcon" size="16" class="mr-1" />
                  Staff Report
                </template>
                <staff-report-tab
                  ref="staffReportTab"
                  :result-data="resultData"
                  :filter-options="filterOptions"
                  :table-show="tableShow"
                  @filter-change="handleFilterChange"
                />
              </b-tab>

              <!-- Manager Summary Report Tab -->
              <b-tab title="Manager Summary Report">
                <template #title>
                  <feather-icon icon="UserCheckIcon" size="16" class="mr-1" />
                  Manager Summary Report
                </template>
                <manager-summary-report-tab
                  ref="managerSummaryReportTab"
                  :result-data="resultData"
                  :filter-options="filterOptions"
                  :table-show="tableShow"
                  @filter-change="handleFilterChange"
                />
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>
      </b-card>

      <template #overlay v-if="download">
        <div class="text-center">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon"> </b-icon>
          <p id="cancel-label">
            <feather-icon icon="DownloadIcon" /> Report creating please wait...
          </p>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCol,
  BRow,
  BTab,
  BTabs,
  BCard,
  BCardHeader,
  BCardBody,
  BAlert,
  BLink,
  BIcon,
  BProgress,
  BPagination,
  BFormSelect,
  VBModal,
} from "bootstrap-vue";

// Import tab components
import DashboardTab from "./report-tabs/DashboardTab.vue";
import ManagerReportTab from "./report-tabs/ManagerReportTab.vue";
import ManagerSummaryReportTab from "./report-tabs/ManagerSummaryReportTab.vue";
import StaffReportTab from "./report-tabs/StaffReportTab.vue";
import SummaryReportTab from "./report-tabs/SummaryReportTab.vue";
import { ref, onUnmounted } from "@vue/composition-api";

import store from "@/store";
import reviewStoreModule from "./reviewStoreModule";
import userStoreModule from "../user/userStoreModule";
import ImageCropper from "@core/components/image-cropper/ImageCropper.vue";
import { required, alphaNum, email } from "@validations";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import Cleave from "vue-cleave-component";
import "cleave.js/dist/addons/cleave-phone.us";
import router from "@/router";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axiosIns from "@/libs/axios";
import _ from "lodash";
import { t } from "@/@core/libs/i18n/utils";
import { backgroundColor } from "echarts/lib/theme/dark";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { $themeColors } from "@themeConfig";
import "animate.css";
import moment from "moment";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

export default {
  components: {
    BTab,
    BTabs,
    BCard,
    BCardHeader,
    BCardBody,
    BAlert,
    BLink,
    BIcon,
    BProgress,
    BRow,
    BCol,
    BPagination,
    BFormSelect,
    vSelect,
    Cleave,
    ToastificationContent,
    ImageCropper,
    ValidationProvider,
    ValidationObserver,
    flatPickr,
    Treeselect,
    DashboardTab,
    ManagerReportTab,
    ManagerSummaryReportTab,
    StaffReportTab,
    SummaryReportTab,
  },

  directives: {
    Ripple,
    "b-modal": VBModal,
  },

  setup() {
    const REVIEW_STORE_MODULE_NAME = "review";
    const USER_STORE_MODULE_NAME = "user";

    // Register review module
    if (!store.hasModule(REVIEW_STORE_MODULE_NAME))
      store.registerModule(REVIEW_STORE_MODULE_NAME, reviewStoreModule);

    // Register user module
    if (!store.hasModule(USER_STORE_MODULE_NAME))
      store.registerModule(USER_STORE_MODULE_NAME, userStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(REVIEW_STORE_MODULE_NAME))
        store.unregisterModule(REVIEW_STORE_MODULE_NAME);
      if (store.hasModule(USER_STORE_MODULE_NAME))
        store.unregisterModule(USER_STORE_MODULE_NAME);
    });
  },

  data: function() {
    return {
      resultData: {
        data: [],
        total_records: 0,
        total_reviews: 0,
        message: "",
      },
      rangeDate: null,
      filterDateStart: null,
      filterDateEnd: null,
      baseURL: store.state.app.baseURL,
      required,
      alphaNum,
      email,
      dropdownLoading: false,
      download: false,
      formShow: false,
      tableShow: false,
      exportReady: false,
      selectedEvent: null,
      eventTypes: [],
      archive: "N",
      activeTabIndex: 0, // Track active tab (0: Dashboard, 1: Summary, 2: Manager, 3: Staff, 4: Manager Summary)

      // Filter options from API
      filterOptions: {
        reviewers: [],
        reviewees: [],
        sections: [],
        statuses: [],
        test_taken_counts: [],
      },

      // Filter values
      reviewerNameFilter: null,
      revieweeNameFilter: null,
      groupNameFilter: null,
      questionNameFilter: null,
      statusFilter: null,
      testTakenCountFilter: null,

      // Department/Site filter
      departments: [],
      selectedDepartmentSites: [], // Array of selected department and site IDs
    };
  },

  computed: {},

  mounted() {
    this.getReviewResults();
    this.getDepartments();
  },

  created() {},

  watch: {
    rangeDate: {
      handler: function(val, before) {
        if (val != null) {
          if (val.includes("to")) {
            this.filterDateStart = this.rangeDate.split(" to ")[0];
            this.filterDateEnd = this.rangeDate.split(" to ")[1];

            this.selectedEvents();
          }
        }
      },
    },

    activeTabIndex: {
      handler: function(newTabIndex, oldTabIndex) {
        // When switching tabs, just reset the currently active tab's filters
        // Don't clear all tabs to avoid multiple API calls
        if (oldTabIndex !== undefined && newTabIndex !== oldTabIndex) {
          // Simply do nothing - let each tab manage its own state independently
          // Each tab will have clean filters when first rendered
        }
      },
    },
  },

  methods: {
    handleFilterChange(filters) {
      // Handle filter changes from report tabs and update global filters
      if (filters.managerName !== undefined) {
        this.reviewerNameFilter = filters.managerName;
      }
      if (filters.reviewerName !== undefined) {
        this.reviewerNameFilter = filters.reviewerName;
      }
      if (filters.revieweeName !== undefined) {
        this.revieweeNameFilter = filters.revieweeName;
      }
      if (filters.staffName !== undefined) {
        this.revieweeNameFilter = filters.staffName; // Staff name maps to reviewee filter
      }
      if (filters.sectionName !== undefined) {
        this.groupNameFilter = filters.sectionName; // Section name maps to group filter
      }
      if (filters.groupName !== undefined) {
        this.groupNameFilter = filters.groupName;
      }
      if (filters.questionName !== undefined) {
        this.questionNameFilter = filters.questionName;
      }
      if (filters.status !== undefined) {
        this.statusFilter = filters.status;
      }
      if (filters.testTakenCount !== undefined) {
        this.testTakenCountFilter = filters.testTakenCount;
      }

      if (this.selectedEvent) {
        this.selectedEvents(this.selectedEvent, filters);
      }
    },

    clearAllFiltersWithoutRefresh() {
      // Reset all filter states to null/empty WITHOUT making API calls
      this.reviewerNameFilter = null;
      this.revieweeNameFilter = null;
      this.groupNameFilter = null;
      this.questionNameFilter = null;
      this.statusFilter = null;
      this.testTakenCountFilter = null;
      this.selectedDepartmentSites = [];

      // Also clear any v-model bindings by setting to empty string
      this.$nextTick(() => {
        this.reviewerNameFilter = "";
        this.revieweeNameFilter = "";
        this.groupNameFilter = "";
        this.questionNameFilter = "";
        this.statusFilter = "";
        this.testTakenCountFilter = "";
      });

      // Force clear filters in child components without refreshing data
      this.$nextTick(() => {
        // Clear filters in SummaryReportTab if it exists
        if (
          this.$refs.summaryReportTab &&
          this.$refs.summaryReportTab.clearAllFilters
        ) {
          this.$refs.summaryReportTab.clearAllFilters();
        }
      });
    },

    clearAllFilters() {
      // Reset all filter states to null/empty
      this.reviewerNameFilter = null;
      this.revieweeNameFilter = null;
      this.groupNameFilter = null;
      this.questionNameFilter = null;
      this.statusFilter = null;
      this.testTakenCountFilter = null;
      this.selectedDepartmentSites = [];

      // Also clear any v-model bindings by setting to empty string
      this.$nextTick(() => {
        this.reviewerNameFilter = "";
        this.revieweeNameFilter = "";
        this.groupNameFilter = "";
        this.questionNameFilter = "";
        this.statusFilter = "";
        this.testTakenCountFilter = "";
      });

      // Refresh data with cleared filters and notify all child components
      if (this.selectedEvent) {
        this.selectedEvents(this.selectedEvent, {});

        // Force clear filters in child components
        this.$nextTick(() => {
          // Clear filters in SummaryReportTab if it exists
          if (
            this.$refs.summaryReportTab &&
            this.$refs.summaryReportTab.clearAllFilters
          ) {
            this.$refs.summaryReportTab.clearAllFilters();
          }

          // Trigger filter change with empty filters
          this.handleFilterChange({
            reviewerName: null,
            revieweeName: null,
            groupName: null,
            questionName: null,
            status: null,
            testTakenCount: null,
          });
        });
      }
    },

    showArchiveRecords() {
      if (this.archive == "Y") {
        this.archive = "N";
        this.selectedEvents();
      } else {
        this.archive = "Y";
        this.selectedEvents();
      }
    },

    async downloadPdf() {
      // Guard against first-load race: require selectedEvent and data to be ready
      if (
        !this.selectedEvent ||
        !(
          this.resultData &&
          Array.isArray(this.resultData.data) &&
          this.resultData.data.length > 0
        ) ||
        !this.exportReady
      ) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Not ready",
            text: "Please wait until data loads before generating the report.",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      this.formShow = true;
      this.download = true;

      let info = {
        surveyName: this.selectedEvent.title,
        date: moment(new Date()).format("DD/MM/YYYY"),
        totalRecords: this.resultData.total_records,
        totalReviews: this.resultData.total_reviews,
        filteredRecords: this.resultData.data.length,
      };

      // Parse selected departments and sites for export
      const departmentIds = [];
      const siteIds = [];

      this.selectedDepartmentSites.forEach((id) => {
        if (id.startsWith("dept_")) {
          departmentIds.push(parseInt(id.replace("dept_", "")));
        } else if (id.startsWith("site_")) {
          siteIds.push(parseInt(id.replace("site_", "")));
        }
      });

      let requestData = {
        surveyId: this.selectedEvent.id,
        filterDate: {
          startDate: this.filterDateStart,
          endDate: this.filterDateEnd,
        },
        archive: this.archive,
        filters: {
          reviewerName:
            this.reviewerNameFilter && this.reviewerNameFilter.trim() !== ""
              ? this.reviewerNameFilter
              : null,
          revieweeName:
            this.revieweeNameFilter && this.revieweeNameFilter.trim() !== ""
              ? this.revieweeNameFilter
              : null,
          groupName:
            this.groupNameFilter && this.groupNameFilter.trim() !== ""
              ? this.groupNameFilter
              : null,
          questionName:
            this.questionNameFilter && this.questionNameFilter.trim() !== ""
              ? this.questionNameFilter
              : null,
          status:
            this.statusFilter && this.statusFilter.trim() !== ""
              ? this.statusFilter
              : null,
          testTakenCount:
            this.testTakenCountFilter &&
            this.testTakenCountFilter !== "" &&
            this.testTakenCountFilter !== null &&
            this.testTakenCountFilter !== undefined
              ? this.testTakenCountFilter
              : null,
        },
        // Add department/site filters for export
        departmentIds: departmentIds.length > 0 ? departmentIds : null,
        siteIds: siteIds.length > 0 ? siteIds : null,
        info: info,
        // Ensure exports always ignore pagination on backend
        pagination: { enabled: false },
      };

      // Determine which report to generate based on active tab
      const reportActions = [
        "review/createSummaryReportPdf", // 0: Dashboard → Overall Summary Report
        "review/createSummaryReportPdf", // 1: Overall Summary Report
        "review/createManagerReportPdf", // 2: Manager Report
        "review/createStaffReportPdf", // 3: Staff Report
        "review/createManagerSummaryReportPdf", // 4: Manager Summary Report
      ];

      const reportNames = [
        "Summary_Report.pdf", // 0: Dashboard → Summary Report
        "Summary_Report.pdf",
        "Manager_Report.pdf",
        "Staff_Report.pdf",
        "Manager_Summary_Report.pdf",
      ];

      const actionToCall =
        reportActions[this.activeTabIndex] || reportActions[1]; // Default to Summary
      const fileName = reportNames[this.activeTabIndex] || reportNames[1];

      store
        .dispatch(actionToCall, requestData)
        .then((response) => {
          if (response.status == 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Successful",
                text: "✔️ Report is created!",
                icon: "ThumbsUpIcon",
                variant: "success",
              },
            });
            this.download = false;
            this.formShow = false;
          } else {
            this.formShow = false;
            this.download = false;
          }
        })
        .catch((error) => {
          this.formShow = false;
          this.download = false;
        });
    },

    getLastEvent() {
      store
        .dispatch("review/getLastEventItem", [])
        .then((res) => {
          if (res.data) {
            this.selectedEvent = res.data;
            this.selectedEvents(res.data);
          }
        })
        .catch((error) => {
          console.log(error);
          this.formShow = false;
        });
    },

    clearDate() {
      if (this.rangeDate != null && this.rangeDate != "") {
        this.filterDateStart = null;
        this.filterDateEnd = null;
        this.rangeDate = null;
        this.selectedEvents();
      }
    },

    getReviewResults() {
      this.formShow = true;

      let filter = {
        event: this.selectedEvent ? this.selectedEvent : null,
        filterDate: {
          startDate: this.filterDateStart,
          endDate: this.filterDateEnd,
        },
      };

      store
        .dispatch("review/getAllReviewResults", filter)
        .then((res) => {
          if (res.data.length > 0) {
            this.summaryList = res.data[0].summary;
            this.getLastEvent();
            res.data.forEach((element) => {
              this.eventTypes.push(element.event);
            });

            this.eventTypes = _.uniqBy(this.eventTypes, function(e) {
              return e.id;
            });

            // Store filter options if available in the initial response
            if (res.data[0] && res.data[0].filter_options) {
              this.filterOptions = {
                reviewers: res.data[0].filter_options.reviewers || [],
                reviewees: res.data[0].filter_options.reviewees || [],
                sections: res.data[0].filter_options.sections || [],
                statuses: res.data[0].filter_options.statuses || [],
                test_taken_counts:
                  res.data[0].filter_options.test_taken_counts || [],
              };
            }
          }

          this.formShow = false;
        })
        .catch((error) => {
          this.formShow = false;
          console.log(error);
        });
    },

    selectedEvents(event, customFilters = null) {
      this.tableShow = true;
      this.exportReady = false;

      // Parse selected departments and sites
      const departmentIds = [];
      const siteIds = [];

      this.selectedDepartmentSites.forEach((id) => {
        if (id.startsWith("dept_")) {
          departmentIds.push(parseInt(id.replace("dept_", "")));
        } else if (id.startsWith("site_")) {
          siteIds.push(parseInt(id.replace("site_", "")));
        }
      });

      let filter = {
        event: event == undefined ? this.selectedEvent : event,
        filterDate: {
          startDate: this.filterDateStart,
          endDate: this.filterDateEnd,
        },
        archive: this.archive,
        filters: customFilters || {
          reviewerName: null,
          revieweeName: null,
          groupName: null,
          questionName: null,
          status: null,
          testTakenCount: null,
        },
        // Add department/site filters
        departmentIds: departmentIds.length > 0 ? departmentIds : null,
        siteIds: siteIds.length > 0 ? siteIds : null,
        // Disable backend pagination for all tabs except Overall Summary Report
        // Other tabs handle pagination on frontend side
        pagination:
          this.activeTabIndex === 1 // Only Overall Summary Report (index 1) uses backend pagination
            ? { enabled: true, page: 1, perPage: 25 }
            : { enabled: false },
      };

      store
        .dispatch("review/getResultsWithEventIdTable", filter)
        .then((res) => {
          if (res.data.message && res.data.message.includes("successfully")) {
            this.resultData = {
              data: res.data.data || [],
              total_records: res.data.total_count || 0,
              total_reviews: res.data.total_surveys || 0,
              message: res.data.message || "",
            };
            // Store filter options from API response
            if (res.data.filter_options) {
              this.filterOptions = {
                reviewers: res.data.filter_options.reviewers || [],
                reviewees: res.data.filter_options.reviewees || [],
                sections: res.data.filter_options.sections || [],
                statuses: res.data.filter_options.statuses || [],
                test_taken_counts:
                  res.data.filter_options.test_taken_counts || [],
              };
            }
            this.$nextTick(() => {
              this.exportReady = true;
            });
          } else {
            this.resultData = {
              data: [],
              total_records: 0,
              total_reviews: 0,
              message: "No data found",
            };
            this.exportReady = false;
          }
          this.tableShow = false;
          this.formShow = false;
        })
        .catch((error) => {
          this.formShow = false;
          this.resultData = {
            data: [],
            total_records: 0,
            total_reviews: 0,
            message: "Error loading data",
          };
          this.exportReady = false;
          console.log(error);
        });
    },

    getDepartments() {
      store
        .dispatch("user/getAllDepartments")
        .then((res) => {
          // Build tree structure: departments with nested sites
          this.departments = res.data.map((department) => ({
            id: `dept_${department.id}`,
            label: department.title,
            children: (department.sites || []).map((site) => ({
              id: `site_${site.id}`,
              label: site.title,
            })),
          }));
        })
        .catch((error) => {
          console.log("Error loading departments:", error);
        });
    },

    onDepartmentSiteChange() {
      // Trigger data refresh when department/site selection changes
      if (this.selectedEvent) {
        this.selectedEvents();
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-wizard.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

// v-select styling for event dropdown
.form-select-sm {
  .vs__dropdown-option {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .vs__selected {
    display: block;
    max-width: calc(100% - 40px); // Leave space for clear button
  }
}

// TreeSelect styling
.treeselect-sm {
  font-size: 0.875rem;

  .vue-treeselect__control {
    min-height: 32px;
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;

    &:hover {
      border-color: #b8b6c1;
    }
  }

  .vue-treeselect__placeholder,
  .vue-treeselect__single-value {
    font-size: 0.875rem;
    line-height: 30px;
  }

  .vue-treeselect__multi-value {
    font-size: 0.75rem;
    margin: 2px;
  }

  .vue-treeselect__input {
    font-size: 0.875rem;
  }
}

.left-align .nav-tabs .nav-link {
  justify-content: left !important;
}

.nav-pills .nav-link {
  margin-bottom: 7px;
  white-space: nowrap;
}

/* Statistical Data Card Styling */
.statistical-data-card {
  .card-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 1px solid #dee2e6;
    border-radius: 0.375rem 0.375rem 0 0 !important;
    padding: 0.75rem 1rem;

    h6 {
      color: #495057 !important;
      font-size: 0.95rem;
    }
  }

  .stats-row {
    margin: 0;
    padding: 0.5rem 0;
  }

  .stat-col {
    padding: 0.25rem 0.5rem;
  }

  .stat-item-compact {
    border-radius: 0.375rem;
    transition: all 0.2s ease;
    border: 1px solid transparent;

    &:hover {
      background-color: rgba(0, 123, 255, 0.03);
      transform: translateY(-1px);
      border-color: rgba(0, 123, 255, 0.1);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .stat-icon-wrapper {
      width: 15px;
      height: 15px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 0.5rem;
    }

    h5 {
      font-weight: 700;
      font-size: 1.1rem;
      line-height: 1.2;
    }

    .stat-label {
      font-weight: 500;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      line-height: 1.2;
    }
  }
}

/* Groups Container Styling */
.groups-container {
  .question-content {
    h6 {
      line-height: 1.4;
      margin-bottom: 0.25rem;
    }

    p {
      line-height: 1.3;
      margin-bottom: 0;
    }
  }
}

/* Card header styling */
.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  border-radius: 0.375rem 0.375rem 0 0 !important;

  h5 {
    color: #495057 !important;
  }

  .badge {
    &.badge-light-info {
      background-color: rgba(23, 162, 184, 0.1) !important;
      color: #17a2b8 !important;
      border: 1px solid rgba(23, 162, 184, 0.2);
    }

    &.badge-light-success {
      background-color: rgba(40, 167, 69, 0.1) !important;
      color: #28a745 !important;
      border: 1px solid rgba(40, 167, 69, 0.2);
    }

    &.badge-light-primary {
      background-color: rgba(0, 123, 255, 0.1) !important;
      color: #007bff !important;
      border: 1px solid rgba(0, 123, 255, 0.2);
    }
  }
}

/* Score status colors */
.bg-light-success {
  background-color: rgba(40, 167, 69, 0.1) !important;
  color: #28a745 !important;
}

.bg-light-danger {
  background-color: rgba(220, 53, 69, 0.1) !important;
  color: #dc3545 !important;
}

.bg-light-warning {
  background-color: rgba(255, 193, 7, 0.1) !important;
  color: #ffc107 !important;
}

/* Empty state styling */
.text-center {
  .feather-icon {
    opacity: 0.5;
  }
}

/* Purple color variants */
.text-purple {
  color: #6f42c1 !important;
}

.bg-light-purple {
  background-color: rgba(111, 66, 193, 0.1) !important;
}

.bg-light-secondary {
  background-color: rgba(108, 117, 125, 0.1) !important;
}

/* Table row styling for failing scores */
.table-danger-row {
  background-color: rgba(220, 53, 69, 0.1) !important;

  &:hover {
    background-color: rgba(220, 53, 69, 0.15) !important;
  }

  td {
    border-top-color: rgba(220, 53, 69, 0.2) !important;
  }
}

/* Controls Section Styling */
.form-select-lg {
  .vs__dropdown-toggle {
    min-height: 21px;
    border: 1px solid #d8d6de;
    border-radius: 0.375rem;

    &:hover {
      border-color: #b8b6c1;
    }
  }

  .vs__selected-options {
    padding: 2px 12px;
  }
}

.btn-report {
  min-height: 38px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.15);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    transform: none;
    box-shadow: none;
  }
}

/* Success Rate Progress Bar Styling */
.success-rate-container {
  min-width: 120px;
  max-width: 150px;

  .progress {
    border-radius: 10px;
    background-color: #f8f9fa;

    .progress-bar {
      border-radius: 10px;
      font-size: 0.75rem;
      font-weight: 600;

      &.bg-success {
        background: linear-gradient(90deg, #28a745 0%, #20c997 100%) !important;
      }

      &.bg-warning {
        background: linear-gradient(90deg, #ffc107 0%, #fd7e14 100%) !important;
      }

      &.bg-info {
        background: linear-gradient(90deg, #17a2b8 0%, #6610f2 100%) !important;
      }

      &.bg-danger {
        background: linear-gradient(90deg, #dc3545 0%, #e83e8c 100%) !important;
      }
    }
  }

  small {
    font-size: 0.7rem;
    line-height: 1.2;
  }
}

/* Clean Filter Styling to match ReviewList */
.mb-40 {
  margin-bottom: 40px;
}

/* Filter Card Styling */
.filter-card {
  border: 1px solid #e3e6f0;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
  border-radius: 0.35rem;

  &.compact {
    .card-body {
      padding: 0.75rem 1rem;
    }
  }

  .card-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 1px solid #e3e6f0;
    padding: 0.5rem 1rem;

    h6 {
      color: #5a5c69;
      font-weight: 600;
      margin: 0;
      font-size: 0.875rem;
    }
  }

  .card-body {
    padding: 1rem;
  }

  /* Compact form group styling */
  .form-group {
    margin-bottom: 0.5rem;

    label {
      font-weight: 500;
      color: #5a5c69;
      font-size: 0.75rem;
      margin-bottom: 0.25rem;
    }
  }

  /* v-select compact styling */
  .v-select.form-select-sm {
    .vs__dropdown-toggle {
      border: 1px solid #d1d3e2;
      border-radius: 0.25rem;
      min-height: 32px;

      &:hover {
        border-color: #a0a0a0;
      }

      &.vs--open {
        border-color: #80bdff;
        box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.25);
      }
    }

    .vs__selected-options {
      padding: 2px 8px;
    }

    .vs__search {
      font-size: 0.8rem;
      padding: 0;
      margin: 0;
    }

    .vs__selected {
      font-size: 0.8rem;
      color: #5a5c69;
      padding: 0;
      margin: 0;
    }

    .vs__actions {
      padding: 2px 8px;
    }
  }

  /* Input styling consistency */
  .form-control {
    border: 1px solid #d1d3e2;
    border-radius: 0.25rem;
    font-size: 0.8rem;

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.25);
    }
  }

  .btn-outline-warning {
    border-color: #f6c23e;
    color: #f6c23e;
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;

    &:hover {
      background-color: #f6c23e;
      border-color: #f6c23e;
      color: #fff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

/* Compact Table Styling */
.compact-table-container {
  .results-table-wrapper {
    background: white;
    border-radius: 0.375rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .compact-results-table {
    font-size: 0.875rem;

    thead th {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 2px solid #dee2e6;
      font-weight: 600;
      font-size: 0.8rem;
      padding: 0.75rem 0.5rem;
      vertical-align: middle;
    }

    tbody td {
      padding: 0.6rem 0.5rem;
      vertical-align: middle;
      border-top: 1px solid #f1f1f1;
    }

    tbody tr:hover {
      background-color: rgba(0, 123, 255, 0.04);
    }

    /* Group Header Row Styling */
    tbody tr.table-group-header {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-top: 2px solid #007bff;
      font-weight: 600;

      &:hover {
        background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
      }

      td {
        padding: 0.8rem 0.5rem;
        border-top: 2px solid #007bff;
        font-weight: 600;
      }
    }

    /* Question Row Styling */
    tbody tr.table-question-row {
      background-color: #fdfdfd;

      &:hover {
        background-color: rgba(0, 123, 255, 0.06);
      }

      td {
        padding: 0.5rem 0.5rem;
        border-top: 1px solid #f8f9fa;
        border-left: 3px solid #e9ecef;
      }
    }
  }

  .question-name-cell {
    .question-text {
      display: block;
      max-width: 300px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.4;
      font-weight: 500;
    }
  }

  .score-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-weight: 600;
    font-size: 0.875rem;
    min-width: 40px;
    text-align: center;

    &.group-score {
      font-size: 0.9rem;
      font-weight: 700;
      padding: 0.3rem 0.6rem;
      background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
    }
  }

  .passing-score-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    background-color: #e9ecef;
    color: #495057;
    font-size: 0.8rem;
    font-weight: 500;

    &.font-weight-bold {
      font-weight: 700;
      background-color: #ffc107;
      color: #212529;
    }
  }

  .status-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.35rem 0.6rem;

    &.group-status {
      font-size: 0.8rem;
      padding: 0.4rem 0.7rem;
      font-weight: 700;
    }
  }

  .status-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
    justify-content: center;

    .compulsory-badge {
      font-size: 0.65rem;
      font-weight: 600;
      padding: 0.25rem 0.4rem;
      background-color: #ffc107 !important;
      color: #212529 !important;
      border: 1px solid #e0a800;
      white-space: nowrap;
    }
  }

  .success-rate-cell {
    .success-rate-text {
      font-weight: 500;
      color: #495057;
    }
  }

  .test-count-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.5rem;
  }

  .completion-date {
    font-size: 0.8rem;
    color: #6c757d;
    font-weight: 500;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filter-card {
    .card-body {
      padding: 1rem 0.75rem;
    }
  }

  .compact-results-table {
    thead th,
    tbody td {
      padding: 0.5rem 0.25rem;
      font-size: 0.8rem;
    }

    .question-name-cell .question-text {
      max-width: 200px;
    }
  }
}

/* Pagination Styling */
.pagination {
  .page-link {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
    border-color: #d1d3e2;
    color: #5a5c69;

    &:hover {
      background-color: #f8f9fa;
      border-color: #d1d3e2;
      color: #5a5c69;
    }
  }

  .page-item.active .page-link {
    background-color: #007bff;
    border-color: #007bff;
  }

  .page-item.disabled .page-link {
    background-color: #f8f9fa;
    border-color: #d1d3e2;
    color: #6c757d;
  }
}

/* Results Table Wrapper Pagination */
.results-table-wrapper {
  .pagination-info {
    font-size: 0.8rem;
    color: #6c757d;
  }
}

/* Page Header Styling */
.text-primary h3 {
  color: #007bff !important;
  font-weight: 600;
}

.page-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}
</style>
